/**
 * Format instants for Portugal (Europe/Lisbon).
 * Backend sends UTC ISO strings; we always display in Lisbon, not the browser's local zone.
 */

export const LISBON_TZ = 'Europe/Lisbon';

const PT_LOCALE = 'pt-PT';

function toDate(input: string | number | Date | null | undefined): Date | null {
  if (input == null || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatInLisbon(
  input: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleString(PT_LOCALE, { timeZone: LISBON_TZ, ...options });
}

/** e.g. 15/01/2026, 14:30:45 */
export function formatDateTimeLisbon(input: string | number | Date | null | undefined): string {
  return formatInLisbon(input, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** Date part only in Lisbon, e.g. 15 de jan. de 2026 */
export function formatDateLisbonMedium(input: string | number | Date | null | undefined): string {
  return formatInLisbon(input, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Time part only in Lisbon */
export function formatTimeLisbonHm(input: string | number | Date | null | undefined): string {
  return formatInLisbon(input, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** e.g. 15 jan., 14:30 — for compact UI (notifications) */
export function formatDateTimeLisbonShort(input: string | number | Date | null | undefined): string {
  return formatInLisbon(input, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Date-only in Lisbon (YYYY-MM-DD calendar day in Portugal). */
export function formatLisbonDateKey(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: LISBON_TZ });
}

/**
 * Last instant (UTC) that still falls on `dateKey` (YYYY-MM-DD) on the Lisbon calendar.
 * Used when the server stores an aware/UTC end-of-poll instant.
 */
export function endOfLisbonCalendarDayUtcIso(dateKey: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey.trim());
  if (!m) return '';
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  let t = new Date(Date.UTC(y, mo - 1, d + 1, 0, 0, 0)).getTime() - 1;
  const floor = new Date(Date.UTC(y, mo - 1, d - 1, 0, 0, 0)).getTime();
  while (t > floor) {
    const inst = new Date(t);
    if (formatLisbonDateKey(inst) === dateKey) {
      return inst.toISOString();
    }
    t -= 60_000;
  }
  return new Date(Date.UTC(y, mo - 1, d, 23, 59, 59, 999)).toISOString();
}

/**
 * Format a YYYY-MM-DD (or ISO) scheduled date as a civil calendar day in Lisbon.
 * Avoids parsing "YYYY-MM-DD" as UTC midnight (which shifts the day in PT).
 */
export function formatCalendarDateInLisbon(ymd: string | null | undefined): string {
  if (!ymd) return 'N/A';
  const datePart = ymd.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) return ymd;
  const y = parseInt(parts[0], 10);
  const mo = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (!y || !mo || !d) return ymd;
  const inst = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
  return inst.toLocaleDateString(PT_LOCALE, {
    timeZone: LISBON_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}
